import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

export interface WasteEntry {
  _id: string;
  branchId: string;
  ingredientId: string;
  ingredientName: string;
  quantity: number;
  unit: string;
  category: 'expired' | 'damaged' | 'spillage' | 'quality_issue' | 'over_production' | 'other';
  reason: string;
  photoUrl?: string;
  estimatedValue: number;
  status: 'pending' | 'approved' | 'rejected';
  recordedBy: string;
  recordedByName: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  createdAt: string;
}

// Fetch waste entries for a branch
export function useWasteEntries(branchId?: string, status?: string) {
  return useQuery({
    queryKey: ['waste', 'entries', branchId, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (status) params.append('status', status);

      const response = await apiClient.get<APIResponse<{ waste: WasteEntry[] }>>(
        `/waste?${params.toString()}`
      );
      return response.data.data.waste;
    },
    enabled: !!branchId,
  });
}

// Fetch single waste entry
export function useWasteEntry(id: string) {
  return useQuery({
    queryKey: ['waste', 'entry', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ entry: WasteEntry }>>(
        `/waste/${id}`
      );
      return response.data.data.entry;
    },
    enabled: !!id,
  });
}

// Fetch waste summary/analytics
export interface WasteSummary {
  totalValue: number;
  totalEntries: number;
  byCategory: Record<string, { count: number; value: number }>;
  topItems: Array<{ ingredientName: string; quantity: number; value: number }>;
}

export function useWasteSummary(branchId?: string, startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['waste', 'summary', branchId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiClient.get<APIResponse<{ summary: WasteSummary }>>(
        `/waste/summary?${params.toString()}`
      );
      return response.data.data.summary;
    },
    enabled: !!branchId,
  });
}
